import { useEffect, useState, useMemo } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../lib/api';
import { useCurrency } from '../store/currency';
import { useIsMobile } from '../hooks/useIsMobile';

const MY_SCENARIO_NAME = 'My Forecast';

function CustomTooltip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--stone)', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
      <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12.5, marginBottom: 2 }}>
          <span style={{ color: p.color, fontWeight: 500 }}>{p.name}</span>
          <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{fmt ? fmt(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function ForecastPage() {
  const { fmt, fmtFull, currency } = useCurrency();
  const isMobile = useIsMobile();
  const [year, setYear] = useState(new Date().getFullYear());
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideCurrent, setOverrideCurrent] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);

  // Per-month category edits: { month: { category: value } }
  const [monthEdits, setMonthEdits] = useState({});
  const [savingMonth, setSavingMonth] = useState(null);

  // The saved scenario id
  const [scenarioId, setScenarioId] = useState(null);

  // Load forecast — use scenario if one exists
  const load = async (sid) => {
    setLoading(true);
    try {
      const fc = sid
        ? await api.getForecast(year, null) // we pass adjustments directly below
        : await api.getForecast(year);
      setForecast(fc);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Load or find existing "My Forecast" scenario on mount
  const initScenario = async () => {
    try {
      const scenarios = await api.getScenarios();
      const existing = scenarios.find(s => s.name === MY_SCENARIO_NAME);
      if (existing) {
        setScenarioId(existing.id);
        // Reconstruct monthEdits from scenario adjustments
        const edits = {};
        (existing.adjustments || []).forEach(adj => {
          // We store as fixed delta where start_month === end_month
          const m = adj.start_month;
          if (!edits[m]) edits[m] = {};
          // We store target value in notes field, or recompute from delta
          if (adj._target !== undefined) {
            edits[m][adj.category] = String(adj._target);
          }
        });
        setMonthEdits(edits);
        return existing.id;
      }
    } catch {}
    return null;
  };

  useEffect(() => {
    initScenario().then(sid => {
      setScenarioId(sid);
      loadForecastWithEdits({});
    });
  }, [year]);

  // Load forecast applying current edits as scenario adjustments
  const loadForecastWithEdits = async (edits) => {
    setLoading(true);
    try {
      const base = await api.getForecast(year);
      const catAvg = base.category_averages || {};

      // Build adjustments — delta = target - avg so backend adds delta to avg = target
      const adjustments = [];
      Object.entries(edits).forEach(([month, cats]) => {
        Object.entries(cats).forEach(([cat, val]) => {
          if (val === '' || isNaN(parseFloat(val))) return;
          const target = parseFloat(val);
          const avg = catAvg[cat] || 0;
          adjustments.push({
            category: cat,
            delta: Math.round((target - avg) * 100) / 100,
            delta_type: 'fixed',
            start_month: parseInt(month),
            end_month: parseInt(month),
          });
        });
      });

      if (adjustments.length > 0) {
        const fc = await api.getForecastWithAdjustments(year, adjustments);
        setForecast(fc);
      } else {
        setForecast(base);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Save edits to a persistent scenario AND reload forecast
  const saveMonthEdits = async (month, cats) => {
    setSavingMonth(month);
    try {
      const newEdits = { ...monthEdits, [month]: cats };
      if (Object.values(cats).every(v => v === '')) delete newEdits[month];
      setMonthEdits(newEdits);

      // Save scenario for persistence
      const base = await api.getForecast(year);
      const catAvg = base.category_averages || {};
      const adjustments = [];
      Object.entries(newEdits).forEach(([m, cs]) => {
        Object.entries(cs).forEach(([cat, val]) => {
          if (val === '' || isNaN(parseFloat(val))) return;
          const target = parseFloat(val);
          const avg = catAvg[cat] || 0;
          adjustments.push({
            category: cat,
            delta: Math.round((target - avg) * 100) / 100,
            delta_type: 'fixed',
            start_month: parseInt(m),
            end_month: parseInt(m),
          });
        });
      });

      let sid = scenarioId;
      if (sid) {
        await api.updateScenario(sid, { name: MY_SCENARIO_NAME, adjustments, description: 'Auto-saved forecast adjustments' });
      } else {
        const sc = await api.createScenario({ name: MY_SCENARIO_NAME, adjustments, description: 'Auto-saved forecast adjustments' });
        sid = sc.id;
        setScenarioId(sid);
      }

      await loadForecastWithEdits(newEdits);
      toast_success();
    } catch (e) {
      console.error(e);
    } finally {
      setSavingMonth(null);
      setExpandedMonth(null);
    }
  };

  const [toastMsg, setToastMsg] = useState('');
  const toast_success = () => { setToastMsg('Saved!'); setTimeout(() => setToastMsg(''), 2000); };

  // Apply overrideCurrent on top of forecast months
  const months = useMemo(() => {
    if (!forecast?.months) return [];
    const catAvg = forecast.category_averages || {};

    return forecast.months.map(m => {
      let { income, expenses, expense_by_category, net } = m;
      const editsForMonth = monthEdits[m.month];

      // Apply local edits to projected months for immediate UI feedback
      if (editsForMonth && !m.is_actual) {
        expense_by_category = { ...expense_by_category };
        Object.entries(editsForMonth).forEach(([cat, val]) => {
          if (val !== '' && !isNaN(parseFloat(val))) {
            expense_by_category[cat] = parseFloat(val);
          }
        });
        expenses = Object.values(expense_by_category).reduce((a, v) => a + v, 0);
      }

      // Override current month
      if (overrideCurrent && m.is_current) {
        const projExp = Object.values(catAvg).reduce((a, v) => a + v, 0);
        expenses = projExp;
        expense_by_category = { ...catAvg };
      }

      net = income - expenses;
      return { ...m, income, expenses, net, expense_by_category };
    }).reduce((acc, m, i) => {
      const cumulative = (acc[i - 1]?.cumulative ?? 0) + m.net;
      acc.push({ ...m, cumulative: Math.round(cumulative * 100) / 100 });
      return acc;
    }, []);
  }, [forecast, overrideCurrent, monthEdits]);

  const adjYearEnd = months[11]?.cumulative ?? 0;
  const adjTotalIncome = months.reduce((a, m) => a + m.income, 0);
  const adjTotalExpenses = months.reduce((a, m) => a + m.expenses, 0);
  const hasEdits = Object.keys(monthEdits).length > 0;

  const chartData = months.map(m => ({
    name: m.month_abbr || m.month_name?.slice(0, 3),
    Income: m.income, Expenses: m.expenses, Cumulative: m.cumulative,
  }));

  return (
    <div className="animate-in">
      {toastMsg && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: 'var(--green)', color: 'white', padding: '10px 20px', borderRadius: 8, fontWeight: 600, fontSize: 13, zIndex: 999 }}>
           {toastMsg}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>Cash Flow Forecast</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>
            Actuals + projections
            {hasEdits && <span style={{ marginLeft: 8, color: 'var(--accent)', fontWeight: 600, fontSize: 12 }}>Edit Custom adjustments active</span>}
          </div>
        </div>
        <div className="flex gap-2 items-center" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setOverrideCurrent(o => !o)}
            style={{
              padding: '6px 14px', fontSize: 12, borderRadius: 6,
              border: '1px solid var(--stone)',
              background: overrideCurrent ? 'var(--ink)' : 'white',
              color: overrideCurrent ? 'white' : 'var(--ink2)',
              cursor: 'pointer', fontWeight: overrideCurrent ? 600 : 400,
            }}
          >
            {overrideCurrent ? ' Showing Projected' : ' Project Current Month'}
          </button>
          {hasEdits && (
            <button
              onClick={() => { setMonthEdits({}); setScenarioId(null); loadForecastWithEdits({}); }}
              style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--stone)', background: 'white', color: 'var(--red)', cursor: 'pointer' }}
            >
               Reset All Adjustments
            </button>
          )}
          {[2024, 2025, 2026].map(y => (
            <button key={y} className={`btn ${year === y ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setYear(y)}>{y}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--text-muted)' }}>Calculating forecast…</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid-4 mb-6">
            <div className="card">
              <div className="stat-label">Year-End Balance</div>
              <div className={`stat-value ${adjYearEnd >= 0 ? 'green' : 'red'}`}>{fmt(adjYearEnd)}</div>
            </div>
            <div className="card">
              <div className="stat-label">Total Income</div>
              <div className="stat-value green">{fmt(adjTotalIncome)}</div>
            </div>
            <div className="card">
              <div className="stat-label">Total Expenses</div>
              <div className="stat-value red">{fmt(adjTotalExpenses)}</div>
            </div>
            <div className="card">
              <div className="stat-label">Avg Monthly Net</div>
              <div className="stat-value gold">{fmt(adjYearEnd / 12)}</div>
            </div>
          </div>

          {/* What's included */}
          <div className="card mb-6" style={{ background: 'var(--off)', border: '1px solid var(--stone)' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}> What this forecast includes</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--green)', marginBottom: 6 }}> Income</div>
                {forecast?.total_income > 0
                  ? <div style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{fmt(forecast.total_income / 12)}/mo from Income page</div>
                  : <div style={{ fontSize: 12, color: 'var(--red)' }}>! No income entries — <a href="/income" style={{ color: 'var(--accent)' }}>add here</a></div>}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--red)', marginBottom: 6 }}> Expenses</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink2)' }}>
                  {Object.keys(forecast?.category_averages || {}).length} categories · {fmt(forecast?.total_expenses / 12)}/mo avg
                  {hasEdits && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>Edit adjusted</span>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gold)', marginBottom: 6 }}>
                  {forecast?.loan_monthly_total > 0 ? '' : '!'} Loan Payments
                </div>
                {forecast?.loan_monthly_total > 0
                  ? <div style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{fmt(forecast.loan_monthly_total)}/mo on projected months</div>
                  : <div style={{ fontSize: 12, color: 'var(--mid)' }}>No active loans — <a href="/loans" style={{ color: 'var(--accent)' }}>add here</a></div>}
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="card mb-6">
            <div className="section-title mb-4">Cumulative Balance Through {year}</div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="balGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={(props) => <CustomTooltip {...props} fmt={fmt} />} />
                <Area type="monotone" dataKey="Cumulative" stroke="var(--accent)" strokeWidth={2.5} fill="url(#balGrad2)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="card mb-6">
            <div className="section-title mb-4">Monthly Income vs Expenses</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} barGap={3}>
                <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={(props) => <CustomTooltip {...props} fmt={fmt} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Income" fill="var(--green)" radius={[4,4,0,0]} maxBarSize={24} opacity={0.85} />
                <Bar dataKey="Expenses" fill="var(--red)" radius={[4,4,0,0]} maxBarSize={24} opacity={0.75} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly table with inline editing */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '14px 16px 10px', fontSize: 12, color: 'var(--mid)' }}>
               Tap any row to expand and edit projected expenses.
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 24 }}></th>
                    <th>Month</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Income</th>
                    <th style={{ textAlign: 'right' }}>Expenses</th>
                    <th style={{ textAlign: 'right' }}>Net</th>
                    <th style={{ textAlign: 'right' }}>Cumulative</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map(m => {
                    const isExpanded = expandedMonth === m.month;
                    const hasMonthEdits = monthEdits[m.month] && Object.values(monthEdits[m.month]).some(v => v !== '');
                    // Local edit state for this expanded month
                    const localCats = isExpanded
                      ? Object.entries(m.expense_by_category || {})
                          .sort(([,a],[,b]) => b - a)
                      : [];

                    return (
                      <>
                        <tr
                          key={m.month}
                          style={{ cursor: 'pointer', background: isExpanded ? 'var(--off)' : hasMonthEdits ? '#f8faff' : '' }}
                          onClick={() => setExpandedMonth(isExpanded ? null : m.month)}
                        >
                          <td style={{ color: 'var(--mid)', fontSize: 11, textAlign: 'center' }}>
                            {isExpanded ? '' : ''}
                          </td>
                          <td style={{ fontWeight: 500 }}>
                            {m.month_name}
                            {hasMonthEdits && <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 6 }}>Edit</span>}
                          </td>
                          <td>
                            {m.is_actual
                              ? <span className="badge badge-green">Actual</span>
                              : m.is_current
                              ? <span className="badge badge-yellow">Current</span>
                              : <span style={{ color: 'var(--mid)', fontSize: 12 }}>Projected</span>}
                          </td>
                          <td className="text-right amount-pos">{fmt(m.income)}</td>
                          <td className="text-right" style={{ color: 'var(--red)', fontWeight: 500 }}>{fmt(m.expenses)}</td>
                          <td className={`text-right ${m.net >= 0 ? 'amount-pos' : 'amount-neg'}`}>{fmt(m.net)}</td>
                          <td className="text-right" style={{ fontWeight: 500, color: m.cumulative >= 0 ? 'var(--accent)' : 'var(--red)' }}>{fmt(m.cumulative)}</td>
                        </tr>

                        {isExpanded && (
                          <tr key={`${m.month}-detail`}>
                            <td colSpan={7} style={{ padding: 0, background: 'var(--off)', borderBottom: '2px solid var(--stone)' }}>
                              <ExpandedMonthEditor
                                month={m}
                                fmt={fmt}
                                fmtFull={fmtFull}
                                currency={currency}
                                catAvg={forecast?.category_averages || {}}
                                savedEdits={monthEdits[m.month] || {}}
                                saving={savingMonth === m.month}
                                onSave={(cats) => saveMonthEdits(m.month, cats)}
                                onCancel={() => setExpandedMonth(null)}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

//  Inline editor component 
function ExpandedMonthEditor({ month, fmt, fmtFull, currency, catAvg, savedEdits, saving, onSave, onCancel }) {
  const isUSD = currency === 'USD';
  const isActual = month.is_actual || month.is_current;
  const cats = Object.entries(month.expense_by_category || {}).sort(([,a],[,b]) => b - a);

  // Local editable values — always in USD internally
  const [vals, setVals] = useState(() => {
    const init = {};
    cats.forEach(([cat, amt]) => {
      init[cat] = savedEdits[cat] !== undefined ? savedEdits[cat] : (isActual ? '' : String(Math.round(amt)));
    });
    return init;
  });

  const totalEdited = cats.reduce((sum, [cat, amt]) => {
    const v = parseFloat(vals[cat]);
    return sum + (isNaN(v) ? amt : v);
  }, 0);

  return (
    <div style={{ padding: '16px 16px 20px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
        <div style={{ flex: '1 1 200px' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>
            {isActual ? 'Actual — ' : 'Edit projected expenses — '}{month.month_name}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--mid)', marginTop: 2 }}>
            {isActual
              ? 'This month has actual data.'
              : <>
                  Enter amounts in <strong>USD</strong> — values are stored in USD and displayed in {currency}.
                  {!isUSD && <span style={{ marginLeft: 6, color: 'var(--accent)' }}>Preview shows {currency} equivalent.</span>}
                </>}
          </div>
        </div>
        {!isActual && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)' }}>
              Total: {fmt(totalEdited)}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => onSave(vals)} disabled={saving}>
              {saving ? 'Saving…' : 'Save to Forecast'}
            </button>
          </div>
        )}
        {isActual && <button className="btn btn-ghost btn-sm" onClick={onCancel}>Close</button>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 8 }}>
        {cats.map(([cat, actualAmt]) => {
          const avg = catAvg[cat] || 0;
          const editedVal = parseFloat(vals[cat]);
          const diff = isActual ? null : (isNaN(editedVal) ? 0 : editedVal - avg);
          // Live preview in display currency
          const previewInCurrency = !isNaN(editedVal) && !isUSD ? fmt(editedVal) : null;
          const avgInCurrency = !isUSD ? fmt(avg) : null;

          return (
            <div key={cat} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: 'white', borderRadius: 7, padding: '10px 12px',
              border: `1px solid ${vals[cat] !== '' && !isActual ? 'var(--accent)' : 'var(--stone)'}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--ink2)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</div>
                {!isActual && avg > 0 && (
                  <div style={{ fontSize: 10.5, color: 'var(--mid)', marginTop: 2 }}>
                    avg ${avg.toFixed(0)} USD{avgInCurrency ? ` · ${avgInCurrency}` : ''}/mo
                  </div>
                )}
              </div>
              {isActual ? (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)' }}>{fmt(actualAmt)}</div>
                  {!isUSD && <div style={{ fontSize: 10.5, color: 'var(--mid)' }}>${actualAmt.toFixed(0)} USD</div>}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {diff !== 0 && diff !== null && !isNaN(diff) && (
                      <span style={{ fontSize: 10.5, color: diff > 0 ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>
                        {diff > 0 ? '' : ''}{fmt(Math.abs(diff))}
                      </span>
                    )}
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--mid)', pointerEvents: 'none' }}>$</span>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={vals[cat]}
                        placeholder={avg.toFixed(0)}
                        onChange={e => setVals(v => ({ ...v, [cat]: e.target.value }))}
                        style={{
                          width: 90, padding: '5px 8px 5px 18px', fontSize: 16,
                          border: `1px solid ${vals[cat] !== '' ? 'var(--accent)' : 'var(--stone)'}`,
                          borderRadius: 5, outline: 'none', textAlign: 'right',
                          background: vals[cat] !== '' ? '#f0f4ff' : 'white',
                        }}
                      />
                    </div>
                    {vals[cat] !== '' && (
                      <button onClick={() => setVals(v => ({ ...v, [cat]: '' }))}
                        style={{ background: 'none', border: 'none', color: 'var(--mid)', cursor: 'pointer', fontSize: 13, padding: 0 }}></button>
                    )}
                  </div>
                  {/* Live currency preview */}
                  {previewInCurrency && vals[cat] !== '' && (
                    <div style={{ fontSize: 10.5, color: 'var(--accent)' }}>≈ {previewInCurrency} {currency}</div>
                  )}
                  {!previewInCurrency && !isUSD && vals[cat] !== '' && (
                    <div style={{ fontSize: 10.5, color: 'var(--mid)' }}>USD</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
